import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent, useEffect } from "react";
import {
  OrderHistory as OrderHistoryView,
  OrderHistoryCopy,
} from "@bluebottlecoffee/design-system/components";
import { useOrders, useUser } from "@chordcommerce/react-autonomy";
import { Order } from "@bluebottlecoffee/design-system/components/lib/types";
import { compareDesc } from "date-fns";
import {
  getStaticAccountPaths,
  getStaticAccountProps,
  PageData,
} from "../../../../lib/sanity/account-shared-queries";
import { AccountPage } from "../../../../components/AccountPage";
import { orderDetailPage } from "../../../../lib/link-builders";
import { chordOrderAdapter } from "../../../../lib/utils";
import { toChordOrder } from "../../../../lib/chord/transformers/chord-order";

export const getStaticProps: GetStaticProps<PageData<OrderHistoryCopy>> =
  getStaticAccountProps("AccountOrderHistory");
export const getStaticPaths: GetStaticPaths = getStaticAccountPaths;

const OrderHistory: FunctionComponent<PageData<OrderHistoryCopy>> = (props) => {
  const { accountMenuCopy, region, lang, pageCopy, giftCardFormCopy } = props;

  // We reverted from using useOrders({ includeDetail: true }) due to page
  // loading timeouts. We now reload the individual order on each detail page
  const { isFetching, loadOrders, orders } = useOrders();
  const { user } = useUser();

  const orderDetailsLinkBuilder = (order: any): string =>
    orderDetailPage({ lang, region }, order.number);

  const completeOrCanceledOrders: Array<Order> = orders
    ?.filter(
      (order) => order.state === "complete" || order.state === "canceled",
    )
    .map((order) => chordOrderAdapter(toChordOrder(order)));

  const sortedCompleteOrCanceledOrders: Order[] = completeOrCanceledOrders.sort(
    (a, b) => compareDesc(a.completedAt, b.completedAt),
  );

  // orders get cached in the browser so we need to reload if a new user logs in
  useEffect(() => {
    loadOrders();
  }, [user.data.id]);

  return (
    <AccountPage {...props} pageTitle={accountMenuCopy.orderHistory}>
      <OrderHistoryView
        copy={pageCopy}
        giftCardFormCopy={giftCardFormCopy}
        isLoading={isFetching}
        orderDetailsLinkBuilder={orderDetailsLinkBuilder}
        orders={sortedCompleteOrCanceledOrders}
      />
    </AccountPage>
  );
};

export default OrderHistory;
